import { z } from "zod";

import { machineTypeSchema } from "../machine-type";
import { machineWithCorrugateTracksWithToleranceSchema } from "../machine-with-corrugate-tracks";

const x6ZFoldSchema = z.object({
	id: z.string(),
	alias: z.string(),
	width: z.number(),
	thickness: z.number(),
	brand: z.number(),
});

export const x6TrackConfigurationSchema = z.object({
	trackNumber: z.number(),
	configuredTrackWidth: z.number().optional(),
	loadedZFold: x6ZFoldSchema.optional(),
});

export const x6MachineSchema = machineWithCorrugateTracksWithToleranceSchema.extend({
	machineType: z.literal(machineTypeSchema.enum.X6),
});

const getLastProofOfLife = (value: unknown): Date => {
	if (value === null || value === undefined) {
		return new Date(0);
	}

	const parsedDate = z.coerce.date().safeParse(value);
	if (parsedDate.success && Number.isNaN(parsedDate.data.getTime()) === false) {
		return parsedDate.data;
	}

	return new Date(0);
};

// TODO @eb remove this function when the X6 aligns with our decided upon schemas
export const prepareX6MachineData = (data: unknown): unknown => {
	if (typeof data !== "object" || data === null) {
		return data;
	}

	const input = data as Record<string, unknown>;
	const securePrintConfigurationInput = input.securePrintConfiguration as { delayLabelRetrieval?: boolean } | undefined;

	const activeStopReason = input.stopReason as Record<string, unknown>;
	let stopReason: {
		stopCode: number;
		message: string;
		arguments: number[];
		isError: boolean;
	} = { stopCode: 0, message: "", arguments: [], isError: false };
	if (typeof activeStopReason === "object" && activeStopReason !== null) {
		const code = (activeStopReason.code as number) ?? 0;
		stopReason = {
			stopCode: code,
			message: (activeStopReason.message as string) ?? "",
			arguments: [activeStopReason.contextValue ?? undefined].filter((a) => a !== undefined).map(Number),
			isError: code > 1000,
		};
	}

	let activeWarnings = Array.isArray(input.activeWarnings) ? input.activeWarnings : [];
	activeWarnings = activeWarnings.map((w: Record<string, unknown>) => ({
		code: w.code,
		message: w.message,
		arguments: [w.contextValue ?? undefined].filter((a) => a !== undefined).map(Number),
	}));

	let workstations = input.workstations;
	if (!Array.isArray(workstations) || workstations.length < 1) {
		const activeErrors = Array.isArray(input.activeAlarms) ? input.activeAlarms : [];

		workstations = [
			{
				number: 1,
				alarms: activeErrors.map((e: Record<string, unknown>) => ({
					code: e.code,
					message: e.message,
					arguments: [e.contextValue ?? undefined].filter((a) => a !== undefined).map(String),
				})),
				warnings: activeWarnings.map((w: Record<string, unknown>) => ({
					...w,
					arguments: Array.isArray(w.arguments) ? w.arguments.map(String) : [],
				})),
				stopReason: {
					code: stopReason.stopCode,
					message: stopReason.message,
					arguments: stopReason.arguments.map(String),
					isError: stopReason.isError,
				},
			},
		];
	}

	return {
		...input,
		machineType: "X6",
		assetId: input.assetId?.toString() ?? "",
		description: input.description ?? "",
		plcVersion: input.plcVersion ?? "",
		iotDevice: {
			deviceId: input.iotDeviceId ?? "",
			deviceName: input.iotEdgeDeviceId ?? "",

			hubName: "N/A",
			moduleId: "N/A",
			moduleVersion: "N/A",
			lastProofOfLife: getLastProofOfLife(input.proofOfLifeUpdated ?? input.utcDateTime),
		},
		stopReason: stopReason,
		activeWarnings: activeWarnings,
		tracks: [],
		securePrintConfiguration: {
			delayLabelRetrieval: securePrintConfigurationInput?.delayLabelRetrieval ?? false,
		},
		ipOrDnsName: input.ipOrDnsName ?? "N/A",
		port: input.port ?? 1,
		workstations,
	};
};

export type X6Machine = z.infer<typeof x6MachineSchema>;
